import React, { useEffect, useRef, useState } from "react";
import Cookies from "js-cookie";
import hotToast from "react-hot-toast";
import api from "../../chat/api";

const ACCEPT = ".pdf,.docx,.doc,.txt,.xlsx,.xls,.csv";

// The current name with the new file's extension, matching what the server saves for keep_name.
const keptName = (current = "", newFile = "") => {
  const stem = current.includes(".") ? current.slice(0, current.lastIndexOf(".")) : current;
  return `${stem}.${newFile.split(".").pop().toLowerCase()}`;
};

const NameChoiceModal = ({ choice, onChange, onCancel, onConfirm }) => {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onCancel();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onCancel]);
  const options = [
    [true, "keep", "Keep the current name", keptName(choice.item.name, choice.file.name)],
    [false, "new", "Use the new file's name", choice.file.name],
  ];
  return (
    <div className="fixed inset-0 z-[9999] bg-[rgba(24,16,28,0.45)] flex items-center justify-center">
      <div role="dialog" aria-modal="true" className="w-[480px] max-w-[92vw] bg-white rounded-xl p-6 shadow-[0_24px_48px_rgba(24,16,28,0.3)] flex flex-col gap-4 font-outfit">
        <div className="flex flex-col gap-[6px]">
          <p className="m-0 text-[18px] font-semibold text-[#18181B]">Which name should this document have?</p>
          <p className="m-0 text-[14px] leading-[1.5] text-[#3F3F46]">
            The new file has a different name. Its folder and tags stay the same either way.
          </p>
        </div>
        <div className="flex flex-col gap-2">
          {options.map(([keep, id, label, value]) => (
            <label
              key={id}
              htmlFor={`kb-version-name-${id}`}
              className={`flex gap-3 p-3 border rounded-lg cursor-pointer ${
                choice.keep === keep ? "border-[#A04A97] bg-[#FBF3FA]" : "border-[#D4D4D8] bg-white hover:bg-[#FAFAFA]"
              }`}
            >
              <input
                id={`kb-version-name-${id}`}
                type="radio"
                name="kb-version-name"
                checked={choice.keep === keep}
                onChange={() => onChange(keep)}
                className="mt-[3px] accent-[#A04A97]"
              />
              <span className="flex-1 min-w-0 flex flex-col gap-[2px]">
                <span className="text-[13px] font-medium text-[#3F3F46]">{label}</span>
                <span className="text-[14px] font-semibold text-[#18181B] break-words">{value}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="px-5 py-2 border border-[#A1A1AA] rounded-md bg-white hover:bg-[#F4F4F5] text-[14px] font-medium text-[#27272A]"
          >
            Cancel
          </button>
          <button
            type="button"
            autoFocus
            onClick={onConfirm}
            className="px-5 py-2 border border-[#A04A97] rounded-md bg-[#A04A97] hover:bg-[#8E3F86] text-white text-[14px] font-medium"
          >
            Upload
          </button>
        </div>
      </div>
    </div>
  );
};

// "Upload new version" shared by Stale Assets and Conflicts: pick a file, choose the name if it differs,
// then put it into the same document. Render `element` once; call `pick({ id, name })` from a button.
const useNewVersionUpload = ({ onUploaded }) => {
  const fileInput = useRef(null);
  const replacing = useRef(null);
  const [nameChoice, setNameChoice] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const pick = (item) => {
    replacing.current = item;
    if (fileInput.current) {
      fileInput.current.value = "";
      fileInput.current.click();
    }
  };

  const upload = async (item, file, keepName) => {
    const toastId = hotToast.loading(`Uploading the new version of “${item.name}”...`);
    setBusyId(item.id);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("keep_name", keepName ? "true" : "false");
      const orgId = Cookies.get("selected_org_id");
      if (orgId) form.append("organization_id", orgId);
      const res = await api.post(`/api/v1/rfp/knowledge-base/documents/${item.id}/replace/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onUploaded?.(item, res.data);
      hotToast.success("New version uploaded. Approve it with View & Ingest once it's processed.", { id: toastId });
    } catch (err) {
      hotToast.error(err?.response?.data?.error || "Couldn't upload the new version.", { id: toastId });
    } finally {
      setBusyId(null);
    }
  };

  const onFilePicked = (event) => {
    const file = event.target.files?.[0];
    const item = replacing.current;
    replacing.current = null;
    if (!file || !item) return;
    if (file.name === item.name) upload(item, file, false);
    else setNameChoice({ item, file, keep: true });
  };

  const element = (
    <>
      <input ref={fileInput} type="file" accept={ACCEPT} className="hidden" onChange={onFilePicked} />
      {nameChoice && (
        <NameChoiceModal
          choice={nameChoice}
          onChange={(keep) => setNameChoice((c) => ({ ...c, keep }))}
          onCancel={() => setNameChoice(null)}
          onConfirm={() => {
            const { item, file, keep } = nameChoice;
            setNameChoice(null);
            upload(item, file, keep);
          }}
        />
      )}
    </>
  );

  return { pick, busyId, element };
};

export default useNewVersionUpload;
